import React, { FC, useMemo } from 'react';
import { get } from 'lodash';
import { compose } from 'react-apollo';
import { withTheme } from 'theming';
import { withState } from 'MoshtixShared/component-context-provider';
import { Dialog } from 'MoshtixShared/component-dialog';
import { Carousel } from 'MoshtixShared/component-carousel';
import { ThemeInterface } from 'Types/theme';
import { configuration as responsiveConfiguration } from 'MoshtixShared/helper-responsive-configuration';
// eslint-disable-next-line import/no-named-as-default
import ResponsiveGridClass from 'MoshtixShared/component-responsive-grid';
import { pixelsToRem } from 'MoshtixShared/helper-pixels-to-rem';

import { OrderInterface, TicketInterface } from '../../types/app';
import { TicketDistroContext } from '../../app/contexts';
import { history } from '../../app/history';

import { Ticket } from './component-ticket';
import styles from './styles';

const { Hide } = new ResponsiveGridClass({ configuration: responsiveConfiguration });

interface TicketDialogProps {
  theme: ThemeInterface;
  match: {
    isExact: boolean;
    params: {
      id: string;
      eventId: string;
      ticketId: string;
    };
    path: string;
    url: string;
  };
  onCloseClick: () => void;
}

const TicketDialogWithoutTheme: FC<TicketDialogProps> = (props) => {
  const orders: OrderInterface[] = get(props, 'ticketDistroContext.state.orders.items', []);
  const assignedTickets: TicketInterface[] = get(props, 'ticketDistroContext.state.assignedTickets.items', []);

  const tickets = useMemo(
    () =>
      orders
        .flatMap((order: OrderInterface) =>
          order.tickets.items.map((ticket: TicketInterface) => (
            <Ticket
              ticket={ticket}
              key={ticket.id}
              order={order}
              event={order.event}
              onCloseClick={props.onCloseClick}
              isViewingAsAssignee={false}
            />
          )),
        )
        .concat(
          assignedTickets.map((ticket: TicketInterface) => (
            <Ticket
              ticket={ticket}
              key={ticket.id}
              order={ticket.order}
              event={ticket.event}
              onCloseClick={props.onCloseClick}
              isViewingAsAssignee
            />
          )),
        ),
    [orders, assignedTickets],
  );

  const handleCarouselChange = ({
    index,
    selectedChild,
    ticketDistroContext,
  }: {
    index: number;
    selectedChild: React.ReactElement;
    ticketDistroContext: any;
  }) => {
    const eventId =
      get<number>(ticketDistroContext, 'state.orders.items[0].event.id', 0) ||
      get<number>(ticketDistroContext, 'state.assignedTickets.items[0].event.id', 0);

    if (!eventId) {
      return;
    }

    const ticketId = selectedChild.key;
    history.push(`/event/${eventId}/ticket/${ticketId}`);
  };

  const commonCarouselProps = {
    key: props.match.params.ticketId,
    childCss: styles.cardBackground,
    selectedKey: props.match.params.ticketId,
    onChange: handleCarouselChange,
  };

  return (
    <Dialog onCloseClick={props.onCloseClick} mobileBreakpoint={800}>
      <Hide below="medium">
        {
          // desktop carousel
        }
        <Carousel
          {...commonCarouselProps}
          carouselHeight={pixelsToRem(440)}
          carouselWidth={pixelsToRem(720)}
          carouselPadding={pixelsToRem(0)}
          noScrolling
        >
          {tickets}
        </Carousel>
      </Hide>
      <Hide above="medium">
        {
          // mobile carousel
        }
        <Carousel
          {...commonCarouselProps}
          carouselHeight="100vh"
          carouselWidth="100vw"
          carouselPadding={pixelsToRem(0)}
        >
          {tickets}
        </Carousel>
      </Hide>
    </Dialog>
  );
};

export const TicketDialog = compose(
  withState({
    contextName: 'ticketDistroContext',
    Context: TicketDistroContext,
  }),
  withTheme,
)(TicketDialogWithoutTheme);
